import {
	ParcelReportConstraintStatus,
	ParcelReportLegendSymbol,
	Unit,
	type ParcelReportLegendItemInput,
	type ParcelReportCheckInput,
	type ParcelReportColumnInput,
	type ParcelReportConstraintInput,
	type ParcelReportDocumentInput,
	type ParcelReportMapInput,
	type ParcelReportMeasureInput,
	type ParcelReportSectionsInput,
	type Zone,
} from "@/graphql"
import {
	endangeredSpeciesLayerDefinitions,
	FLOODABLE_ZONES_COLOR,
	WETLANDS_COLOR,
} from "@/components/map/environmentLayers"
import { PLANNING_POLICY_COLORS, ZONING_COLOR } from "@/components/map/planningLayers"
import { formatInscriptionNumber, formatQuantity } from "@/utils/format"
import { PARCEL_OUTLINE_COLOR, ParcelMapView, SATELLITE_PARCEL_OUTLINE_COLOR } from "@/utils/parcelMapSnapshot"
import { ParcelReportMetric, parcelReportProfile } from "@/utils/parcelReportProfile"
import { zoneDetails } from "@/utils/parcelZoning"

type ReportTranslate = (key: string, named?: Record<string, unknown>, plural?: number) => string

export type ParcelReportImages = Partial<Record<ParcelMapView, { image: string; widthInMeters: number }>>

interface ParcelReportUnits {
	system: Unit
	areaSymbol: string
	distanceSymbol: string
	toArea: (squareMeters: number) => number
	toDistance: (meters: number) => number
}

interface ParcelReportDocumentOptions {
	parcelId: string
	item: Record<string, unknown>
	zones: Zone[]
	images: ParcelReportImages
	t: ReportTranslate
	units: ParcelReportUnits
	locale: string
	today: Date
	parcelUrl: string
}

type Finding = boolean | null

enum SoilRehabilitationState {
	NotStarted = "Non débutée",
	Initiated = "Initiée",
	Unknown = "Inconnue",
	Completed = "Terminée",
	NotRequired = "Non nécessaire",
}

const SOIL_REHABILITATION_STATES: Array<{ state: SoilRehabilitationState; label: string; isRehabilitated: boolean }> = [
	{
		state: SoilRehabilitationState.NotStarted,
		label: "parcelReport.rehabilitation.notStarted",
		isRehabilitated: false,
	},
	{ state: SoilRehabilitationState.Initiated, label: "parcelReport.rehabilitation.initiated", isRehabilitated: false },
	{ state: SoilRehabilitationState.Unknown, label: "parcelReport.rehabilitation.unknownState", isRehabilitated: false },
	{ state: SoilRehabilitationState.Completed, label: "parcelReport.rehabilitation.completed", isRehabilitated: true },
	{
		state: SoilRehabilitationState.NotRequired,
		label: "parcelReport.rehabilitation.notRequired",
		isRehabilitated: true,
	},
]

const MAX_ZONING_CLASSES = 3

const SATELLITE_VIEWS = new Set([ParcelMapView.CoverLocation, ParcelMapView.CloseUp])

const POLICY_FLAGS = [
	{
		key: "policy_tod_area",
		name: "name_tod",
		label: "parcelCard.badges.tod",
		detail: "parcelReport.planning.tod",
		color: PLANNING_POLICY_COLORS.tod,
	},
	{
		key: "policy_ppu_area",
		name: "name_ppu",
		label: "parcelCard.badges.ppu",
		detail: "parcelReport.planning.ppu",
		color: PLANNING_POLICY_COLORS.ppu,
	},
	{
		key: "policy_corridor_area",
		name: "name_corridor",
		label: "parcelCard.badges.corridor",
		detail: "parcelReport.planning.corridor",
		color: PLANNING_POLICY_COLORS.corridor,
	},
] as const

function isMissing(value: unknown): boolean {
	return value === null || value === undefined || value === "" || value === "null"
}

function reportNumber(value: unknown, maximumFractionDigits = 0): string | null {
	const numberValue = Number(value)
	return isMissing(value) || !Number.isFinite(numberValue)
		? null
		: formatQuantity(numberValue, { maximumFractionDigits })
}

function reportText(value: unknown): string | null {
	return isMissing(value) ? null : String(value)
}

function listItems(value: unknown): string[] {
	return isMissing(value)
		? []
		: String(value)
				.split(",")
				.map(item => item.trim())
				.filter(Boolean)
}

function finding(value: unknown): Finding {
	return value === true ? true : value === false ? false : null
}

function agriculturalZoningFinding(value: unknown): Finding {
	const code = String(value ?? "")
	return ["1", "2"].includes(code) ? true : code === "0" ? false : null
}

function rows(entries: Array<[label: string, value: string | null]>): ParcelReportColumnInput["rows"] {
	return entries.flatMap(([label, value]) => (value === null ? [] : [{ label, value }]))
}

class ParcelReportBuilder {
	private readonly item: Record<string, unknown>
	private readonly t: ReportTranslate

	constructor(private readonly options: ParcelReportDocumentOptions) {
		this.item = options.item
		this.t = options.t
	}

	private map(
		view: ParcelMapView,
		caption: string,
		layers: ParcelReportLegendItemInput[] = []
	): ParcelReportMapInput | null {
		const capture = this.options.images[view]
		if (!capture) return null
		const isSatellite = SATELLITE_VIEWS.has(view)
		const parcel = {
			label: this.t("parcelReport.mapLegend.parcel"),
			color: isSatellite ? SATELLITE_PARCEL_OUTLINE_COLOR : PARCEL_OUTLINE_COLOR,
			symbol: ParcelReportLegendSymbol.Outline,
		}
		return {
			caption,
			attribution: isSatellite ? this.t("parcelReport.satelliteMapAttribution") : this.t("parcelReport.mapAttribution"),
			image: capture.image,
			widthInMeters: capture.widthInMeters,
			legend: [parcel, ...layers],
		}
	}

	private layer(label: string, color: string): ParcelReportLegendItemInput {
		return { label, color, symbol: ParcelReportLegendSymbol.Fill }
	}

	private area(squareMeters: unknown): { value: string; unit: string } | null {
		const value = Number(squareMeters)
		if (isMissing(squareMeters) || !Number.isFinite(value)) return null
		const { toArea, areaSymbol } = this.options.units
		return { value: formatQuantity(Math.round(toArea(value))), unit: areaSymbol }
	}

	private distance(meters: unknown): { value: string; unit: string } | null {
		const value = Number(meters)
		if (isMissing(meters) || !Number.isFinite(value)) return null
		const { toDistance, distanceSymbol } = this.options.units
		return { value: formatQuantity(toDistance(value), { maximumFractionDigits: 1 }), unit: distanceSymbol }
	}

	private withUnit(measure: { value: string; unit: string } | null): string | null {
		return measure && `${measure.value} ${measure.unit}`
	}

	private get policies(): Array<{ label: string; name: string | null; detail: string; color: string }> {
		const policies = POLICY_FLAGS.filter(flag => this.item[flag.key] === true).map(flag => ({
			label: this.t(flag.label),
			name: reportText(this.item[flag.name]),
			detail: this.t(flag.detail),
			color: flag.color as string,
		}))
		if (this.item.policy_spi_area === true || this.item.policy_planif_area === true) {
			policies.push({
				label: this.t("parcelCard.badges.strategicSector"),
				name: reportText(this.item.name_spi ?? this.item.name_planif),
				detail: this.t("parcelReport.planning.strategicSector"),
				color: PLANNING_POLICY_COLORS.strategic,
			})
		}
		return policies
	}

	private share(percent: unknown): string {
		const value = reportNumber(percent, 1)
		return value ? this.t("parcelReport.constraints.parcelShare", { percent: value }) : ""
	}

	private coverConstraints(): ParcelReportConstraintInput[] {
		const t = this.t
		const constraint = (status: ParcelReportConstraintStatus, label: string, detail: string) => ({
			status,
			label,
			detail,
		})
		const byFinding = (state: Finding, label: string, presentDetail: string, absentDetail: string) => {
			if (state === true) return constraint(ParcelReportConstraintStatus.Watch, label, presentDetail)
			if (state === false) return constraint(ParcelReportConstraintStatus.Verified, label, absentDetail)
			return null
		}
		const wetlandArea = this.withUnit(this.area(this.item.env_wetland_area))
		const findings = [
			byFinding(
				finding(this.item.env_floodplain_bool),
				t("parcelReport.constraints.floodZone"),
				t("parcelReport.constraints.possible"),
				t("parcelReport.constraints.outsideZone")
			),
			byFinding(
				finding(this.item.env_wetland_bool),
				t("parcelReport.constraints.wetlands"),
				[this.share(this.item.env_wetland_per), wetlandArea].filter(Boolean).join(" · "),
				t("parcelReport.constraints.none")
			),
			byFinding(
				finding(this.item.env_species_bool),
				t("parcelReport.constraints.threatenedSpecies"),
				[
					listItems(this.item.env_protected_animals).length ? t("parcelReport.planning.fauna") : null,
					listItems(this.item.env_protected_plants).length ? t("parcelReport.planning.flora") : null,
				]
					.filter(Boolean)
					.join(" · "),
				t("parcelReport.constraints.noneListed")
			),
			byFinding(
				agriculturalZoningFinding(this.item.agricultural_zoning),
				t("parcelReport.constraints.agriculturalZone"),
				t("parcelReport.constraints.inZone"),
				t("parcelReport.constraints.outsideZone")
			),
			byFinding(
				finding(this.item.env_rainette_bool),
				t("parcelReport.constraints.chorusFrog"),
				t("parcelReport.constraints.listed"),
				t("parcelReport.constraints.noneListed")
			),
			this.soilContaminationConstraint(),
		].filter(entry => entry !== null)
		const policies = this.policies.length
			? this.policies.map(policy =>
					constraint(
						ParcelReportConstraintStatus.Policy,
						[policy.label, policy.name].filter(Boolean).join(" "),
						t("parcelReport.constraints.landUsePlan")
					)
				)
			: [
					constraint(
						ParcelReportConstraintStatus.Verified,
						t("parcelReport.constraints.planningPolicies"),
						t("parcelReport.constraints.noPolicy")
					),
				]
		const order = [
			ParcelReportConstraintStatus.Watch,
			ParcelReportConstraintStatus.Rehabilitated,
			ParcelReportConstraintStatus.Policy,
			ParcelReportConstraintStatus.Verified,
		]
		return [...findings, ...policies].sort(
			(first, second) => order.indexOf(first.status) - order.indexOf(second.status)
		)
	}

	private soilRehabilitation(): { status: ParcelReportConstraintStatus; detail: string } {
		const t = this.t
		const states = listItems(this.item.env_soil_contaminated_rehab)
		const known = SOIL_REHABILITATION_STATES.filter(({ state }) => states.includes(state))
		const unknown = states.filter(state => !SOIL_REHABILITATION_STATES.some(known => known.state === state))
		const isRehabilitated = states.length > 0 && !unknown.length && known.every(state => state.isRehabilitated)
		const labels = [...known.map(({ label }) => t(label)), ...unknown]
		return {
			status: isRehabilitated ? ParcelReportConstraintStatus.Rehabilitated : ParcelReportConstraintStatus.Watch,
			detail: labels.length
				? t("parcelReport.rehabilitation.detail", { states: labels.join(" · ") })
				: t("parcelReport.rehabilitation.unknown"),
		}
	}

	private soilContaminationConstraint(): ParcelReportConstraintInput | null {
		const state = finding(this.item.env_soil_contaminated_bool)
		const label = this.t("parcelReport.constraints.soilContamination")
		if (state === null) return null
		if (!state)
			return {
				status: ParcelReportConstraintStatus.Verified,
				label,
				detail: this.t("parcelReport.constraints.noneListed"),
			}
		return { label, ...this.soilRehabilitation() }
	}

	private soilContaminationChecks(): ParcelReportCheckInput[] {
		const state = finding(this.item.env_soil_contaminated_bool)
		if (state === null) return []
		if (!state)
			return [this.verifiedCheck("parcelReport.checks.noContamination", "parcelReport.checks.contaminationRegistry")]
		return [{ title: this.t("parcelReport.checks.contamination"), ...this.soilRehabilitation() }]
	}

	private cover(): ParcelReportDocumentInput["cover"] {
		const t = this.t
		const item = this.item
		const profile = parcelReportProfile(item.asset_type, item.asset_category)
		const area = this.area(item.official_area_m2)
		const frontage = this.distance(item.frontage_m)
		const isInAgriculturalZone = agriculturalZoningFinding(item.agricultural_zoning) === true
		const keyFigures: Record<ParcelReportMetric, { label: string; value: string | null; unit?: string }> = {
			[ParcelReportMetric.MunicipalAssessment]: {
				label: t("parcelReport.municipalAssessment"),
				value: reportNumber(item.total_property_value),
				unit: "$",
			},
			[ParcelReportMetric.Area]: { label: t("parcelReport.area"), value: area?.value ?? null, unit: area?.unit },
			[ParcelReportMetric.Frontage]: {
				label: t("parcelCard.frontage"),
				value: frontage?.value ?? null,
				unit: frontage?.unit,
			},
			[ParcelReportMetric.Dwellings]: {
				label: t("parcelReport.dwellings"),
				value: reportNumber(item.number_of_dwelling_units),
			},
			[ParcelReportMetric.AgriculturalZone]: {
				label: t("parcelReport.constraints.agriculturalZone"),
				value: isInAgriculturalZone ? t("parcelReport.constraints.inZone") : null,
			},
		}
		const constraints = this.coverConstraints()
		const counts = (
			[
				[ParcelReportConstraintStatus.Watch, "watch"],
				[ParcelReportConstraintStatus.Rehabilitated, "rehabilitated"],
				[ParcelReportConstraintStatus.Policy, "policy"],
				[ParcelReportConstraintStatus.Verified, "verified"],
			] as const
		)
			.map(([status, key]) => [key, constraints.filter(constraint => constraint.status === status).length] as const)
			.filter(([, count]) => count > 0)
		return {
			parcelNumber: formatInscriptionNumber(item.parcel_id as string),
			address: reportText(item.parcel_address) ?? "",
			locality: [item.mun_name, item.province].filter(Boolean).join(", "),
			zoneCode: reportText(item.zone_code),
			locationMap: this.map(ParcelMapView.CoverLocation, t("parcelReport.mapTitle")),
			keyFigures: profile.metrics.flatMap(metric => {
				const { label, value, unit } = keyFigures[metric]
				return value === null ? [] : [{ label, value, unit }]
			}),
			constraintsSummaryDetail: counts
				.map(([key, count]) => t(`parcelReport.summaryCounts.${key}`, { n: count }, count))
				.join(" · "),
			constraints,
		}
	}

	private lotSection(): ParcelReportSectionsInput["lot"] {
		const t = this.t
		const item = this.item
		const profile = parcelReportProfile(item.asset_type, item.asset_category)
		const ownerTypes: Record<string, string> = {
			"1": t("parcelCard.ownerTypeIndividual"),
			"2": t("parcelCard.ownerTypeCorporate"),
		}
		const ownerType = reportText(item.owner_type)
		const columns: ParcelReportColumnInput[] = [
			{
				title: t("parcelReport.lot.identification"),
				rows: rows([
					[t("parcelReport.lot.lotNumber"), formatInscriptionNumber(item.parcel_id as string)],
					[t("parcelCard.address"), reportText(item.parcel_address)],
					[t("parcelCard.municipality"), reportText(item.mun_name)],
					[t("parcelCard.province"), reportText(item.province)],
				]),
			},
			{
				title: t("parcelCard.accordions.property"),
				rows: rows([
					[t("parcelCard.cadastralArea"), this.withUnit(this.area(item.official_area_m2))],
					[t("parcelCard.frontage"), this.withUnit(this.distance(item.frontage_m))],
					[t("parcelCard.propertyUse"), reportText(item.property_use_label)],
					[t("parcelCard.ownerType"), ownerType && (ownerTypes[ownerType] ?? ownerType)],
					[t("parcelCard.unitCount"), reportNumber(item.unit_count)],
					[t("parcelCard.rollInscriptions"), reportNumber(item.owner_count)],
					[
						t("parcelCard.servicedVacantLand"),
						profile.includeServicedVacantLand && !isMissing(item.serviced_vacant_land)
							? t(
									item.serviced_vacant_land === true || item.serviced_vacant_land === "true"
										? "parcelCard.yes"
										: "parcelCard.no"
								)
							: null,
					],
				]),
			},
		]
		if (profile.includeBuildingDetails) {
			columns.push({
				title: t("parcelCard.accordions.building"),
				rows: rows([
					[t("parcelCard.constructionYear"), reportText(item.construction_year)],
					[t("parcelCard.storeys"), reportNumber(item.number_of_storeys)],
					[t("parcelCard.floorArea"), this.withUnit(this.area(item.floor_area_m2))],
					[t("parcelCard.constructionType"), reportText(item.construction_type_label)],
					[t("parcelCard.physicalLink"), reportText(item.physical_link_label)],
					[t("parcelCard.dwellingUnits"), reportNumber(item.number_of_dwelling_units)],
					[t("parcelCard.nonResidentialUnits"), reportNumber(item.number_of_non_residential_units)],
					[t("parcelCard.rentalRooms"), reportNumber(item.number_of_rental_rooms)],
					[t("parcelCard.nonResidentialClass"), reportText(item.non_residential_property_class)],
					[t("parcelCard.industrialClass"), reportText(item.industrial_class)],
					[
						t("parcelCard.buildingValue"),
						reportNumber(item.building_value) && `${reportNumber(item.building_value)} $`,
					],
				]),
			})
		}
		columns.push({
			title: t("parcelReport.lot.agriculturalLand"),
			rows: rows([
				[t("parcelCard.soilCapability"), reportText(item.env_soil_capability)],
				[t("parcelCard.agriculturalZoneArea"), this.withUnit(this.area(item.eae_agricultural_zone_area_m2))],
				[t("parcelCard.forestZoneArea"), this.withUnit(this.area(item.svfe_agricultural_zone_area_m2))],
				[t("parcelCard.maxTaxableArea"), this.withUnit(this.area(item.eae_max_taxable_area_m2))],
			]),
		})
		return {
			title: t("parcelReport.lot.title"),
			description: t("parcelReport.lot.description"),
			source: t("parcelReport.source"),
			map: this.map(ParcelMapView.CloseUp, t("parcelReport.figures.closeUp")),
			columns: columns.filter(column => column.rows.length),
		}
	}

	private zoningSection(): ParcelReportSectionsInput["zoning"] {
		const t = this.t
		const zones = zoneDetails(this.options.zones, t)
		const [mainZone] = zones
		const policies = this.policies
		if (!mainZone && !policies.length) return null
		const base = {
			title: t("parcelReport.zoning.title"),
			source: t("parcelReport.zoning.source"),
			policies: policies.length
				? {
						title: t("parcelReport.figures.planningPolicies"),
						map: this.map(
							ParcelMapView.PlanningPolicy,
							t("parcelReport.figures.planningPolicies"),
							policies.map(policy => this.layer(policy.label, policy.color))
						),
						items: policies.map(policy => ({
							title: [policy.label, policy.name].filter(Boolean).join(" "),
							detail: policy.detail,
						})),
					}
				: null,
			checks: policies.length
				? []
				: [this.verifiedCheck("parcelReport.checks.noPlanningPolicy", "parcelReport.checks.landUsePlans")],
		}
		if (!mainZone) return { ...base, norms: [], note: t("parcelReport.zoningMapUnavailable") }
		const summary = [mainZone.zoneCode, mainZone.blocks[0]?.title].filter(Boolean).join(" · ")
		const isTruncated = zones.some(zone => zone.blocks.length > MAX_ZONING_CLASSES)
		const truncatedNote = isTruncated ? t("parcelReport.zoning.truncatedClasses", { n: MAX_ZONING_CLASSES }) : null
		return {
			...base,
			summary: truncatedNote ? `${summary}. ${truncatedNote}` : summary,
			normsNote: truncatedNote
				? {
						text: truncatedNote,
						link: mainZone.gridUrl ? { label: t("parcelReport.zoning.gridLink"), url: mainZone.gridUrl } : null,
					}
				: null,
			gridLink: mainZone.gridUrl ? { label: t("zoning.viewGrid"), url: mainZone.gridUrl } : null,
			map: this.map(ParcelMapView.Zoning, t("parcelReport.figures.zoning", { zone: mainZone.zoneCode }), [
				this.layer(t("parcelReport.mapLegend.parcelZone", { zone: mainZone.zoneCode }), ZONING_COLOR),
			]),
			norms: zones.flatMap(zone => {
				const prefix = zones.length > 1 ? `${zone.zoneCode} · ` : ""
				return [
					{ title: `${prefix}${t("zoning.title")}`, rows: zone.rows },
					...zone.blocks
						.slice(0, MAX_ZONING_CLASSES)
						.map(block => ({ title: `${prefix}${block.title}`, rows: block.rows })),
				].filter(column => column.rows.length)
			}),
		}
	}

	private valueSection(): ParcelReportSectionsInput["value"] {
		const t = this.t
		const item = this.item
		const assessment = reportNumber(item.total_property_value)
		if (assessment === null) return null
		const total = Number(item.total_property_value)
		const previous = Number(item.previous_total_property_value)
		const land = Number(item.land_value)
		const building = Number(item.building_value)
		const amount = (value: number) => `${formatQuantity(Math.round(value))} $`
		const hasPrevious = !isMissing(item.previous_total_property_value) && previous > 0 && Number.isFinite(total)
		const hasSplit = Number.isFinite(land) && Number.isFinite(building) && land >= 0 && building > 0
		const year = reportText(item.assessment_roll_year)
		const referenceDate = this.longDate(item.market_reference_date)
		const change = total - previous
		const landShare = hasSplit ? land / (land + building) : 0
		return {
			title: t("parcelReport.value.title"),
			description: year
				? referenceDate
					? t("parcelReport.value.descriptionWithDate", { year, date: referenceDate })
					: t("parcelReport.value.description", { year })
				: null,
			source: t("parcelReport.source"),
			assessment: {
				label: t("parcelReport.municipalAssessment"),
				value: assessment,
				unit: "$",
			},
			change: hasPrevious
				? {
						value: `${change >= 0 ? "+" : "−"}${formatQuantity(Math.abs((change / previous) * 100), {
							minimumFractionDigits: 1,
							maximumFractionDigits: 1,
						})} %`,
						detail: t("parcelReport.value.changeDetail", {
							amount: `${change >= 0 ? "+" : "−"}${amount(Math.abs(change))}`,
						}),
						isIncrease: change >= 0,
					}
				: null,
			split: hasSplit
				? {
						title: t("parcelReport.value.split"),
						detail: t("parcelReport.value.splitDetail", {
							land: Math.round(landShare * 100),
							building: 100 - Math.round(landShare * 100),
						}),
						parts: [
							{ label: t("parcelReport.value.land"), value: amount(land), share: landShare },
							{ label: t("parcelReport.value.building"), value: amount(building), share: 1 - landShare },
						],
					}
				: null,
			history: hasPrevious
				? {
						title: t("parcelReport.value.history"),
						rows: [
							{ label: t("parcelReport.value.previousRoll"), value: amount(previous), amount: previous },
							{
								label: year ? t("parcelReport.value.currentRoll", { year }) : t("parcelReport.municipalAssessment"),
								value: amount(total),
								amount: total,
							},
						],
					}
				: null,
		}
	}

	private longDate(value: unknown): string | null {
		if (isMissing(value)) return null
		const date = new Date(value instanceof Date ? value : String(value))
		if (Number.isNaN(date.getTime())) return null
		return new Intl.DateTimeFormat(this.options.locale, {
			year: "numeric",
			month: "long",
			day: "numeric",
			timeZone: "UTC",
		}).format(date)
	}

	private verifiedCheck(title: string, detail: string): ParcelReportCheckInput {
		return { status: ParcelReportConstraintStatus.Verified, title: this.t(title), detail: this.t(detail) }
	}

	private check(state: Finding, watch: string, verified: string, detail: string): ParcelReportCheckInput[] {
		if (state === null) return []
		if (!state) return [this.verifiedCheck(verified, detail)]
		return [{ status: ParcelReportConstraintStatus.Watch, title: this.t(watch), detail: this.t(detail) }]
	}

	private measure(
		state: Finding,
		label: string,
		map: ParcelReportMapInput | null,
		share?: { percent: unknown; area: unknown }
	): ParcelReportMeasureInput[] {
		if (state !== true) return []
		const areaValue = share ? this.withUnit(this.area(share.area)) : null
		const parcelArea = this.withUnit(this.area(this.item.official_area_m2))
		return [
			{
				status: ParcelReportConstraintStatus.Watch,
				value: share ? reportNumber(share.percent, 1) ?? "—" : null,
				unit: share ? "%" : null,
				label: this.t(label),
				detail:
					areaValue && parcelArea
						? this.t("parcelReport.environment.areaOfParcel", { area: areaValue, total: parcelArea })
						: "",
				map,
			},
		]
	}

	private environmentSection(): ParcelReportSectionsInput["environment"] {
		const t = this.t
		const item = this.item
		const floodplain = finding(item.env_floodplain_bool)
		const wetland = finding(item.env_wetland_bool)
		const species = finding(item.env_species_bool)
		return {
			title: t("parcelReport.environment.title"),
			description: t("parcelReport.environment.description"),
			source: t("parcelReport.environment.source"),
			measures: [
				...this.measure(
					floodplain,
					"parcelReport.environment.possibleFloodZone",
					this.map(ParcelMapView.FloodableZones, t("parcelReport.figures.floodZones"), [
						this.layer(t("parcelReport.figures.floodZones"), FLOODABLE_ZONES_COLOR),
					])
				),
				...this.measure(
					wetland,
					"parcelReport.environment.wetlandShare",
					this.map(ParcelMapView.Wetlands, t("parcelReport.figures.wetlands"), [
						this.layer(t("parcelReport.figures.wetlands"), WETLANDS_COLOR),
					]),
					{ percent: item.env_wetland_per, area: item.env_wetland_area }
				),
			],
			species:
				species === true
					? {
							map: this.map(
								ParcelMapView.EndangeredSpecies,
								t("parcelReport.figures.species"),
								endangeredSpeciesLayerDefinitions.map(({ labelKey, fillColor }) => this.layer(t(labelKey), fillColor))
							),
							groups: [
								{ title: t("parcelReport.planning.fauna"), items: listItems(item.env_protected_animals) },
								{ title: t("parcelReport.planning.flora"), items: listItems(item.env_protected_plants) },
							],
						}
					: null,
			checks: [
				...(floodplain === false
					? [this.verifiedCheck("parcelReport.checks.outsideFloodZone", "parcelReport.checks.melccfp")]
					: []),
				...(wetland === false
					? [this.verifiedCheck("parcelReport.checks.noWetlands", "parcelReport.checks.melccfp")]
					: []),
				...(species === false
					? [this.verifiedCheck("parcelReport.checks.noThreatenedSpecies", "parcelReport.checks.melccfp")]
					: []),
				...this.check(
					agriculturalZoningFinding(item.agricultural_zoning),
					"parcelReport.checks.inAgriculturalZone",
					"parcelReport.checks.outsideAgriculturalZone",
					"parcelReport.checks.cptaq"
				),
				...this.check(
					finding(item.env_rainette_bool),
					"parcelReport.checks.chorusFrog",
					"parcelReport.checks.noChorusFrog",
					"parcelReport.checks.chorusFrogHabitat"
				),
				...this.soilContaminationChecks(),
			],
		}
	}

	private closing(): ParcelReportSectionsInput["closing"] {
		const t = this.t
		const date = new Intl.DateTimeFormat(this.options.locale, {
			year: "numeric",
			month: "long",
			day: "numeric",
		}).format(this.options.today)
		return {
			disclaimer: `${t("parcelReport.disclaimer")} ${t("parcelReport.generatedOn", { date })}`,
			link: { label: t("parcelReport.openInLanderz"), url: this.options.parcelUrl },
			qrDescription: t("parcelReport.qrDescription"),
		}
	}

	build(): ParcelReportDocumentInput {
		const t = this.t
		return {
			parcelID: this.options.parcelId,
			labels: {
				report: t("parcelReport.title"),
				cadastralLot: t("parcelReport.cadastralLot"),
				zone: t("zoning.zone"),
				constraintsSummary: t("parcelReport.constraintsSummary"),
				constraintStatuses: {
					watch: t("parcelReport.statuses.watch"),
					rehabilitated: t("parcelReport.statuses.rehabilitated"),
					policy: t("parcelReport.statuses.policy"),
					verified: t("parcelReport.statuses.verified"),
				},
				source: t("parcelReport.source"),
				brand: "landerz.ca",
				contents: t("parcelReport.contents"),
				scaleUnits: {
					meter: t("unitSystem.distance.meters"),
					kilometer: t("parcelReport.scaleUnits.kilometer"),
					foot: t("unitSystem.distance.feet"),
					mile: t("parcelReport.scaleUnits.mile"),
				},
			},
			unitSystem: this.options.units.system,
			cover: this.cover(),
			sections: {
				lot: this.lotSection(),
				zoning: this.zoningSection(),
				value: this.valueSection(),
				environment: this.environmentSection(),
				closing: this.closing(),
			},
		}
	}
}

export function buildParcelReportDocument(options: ParcelReportDocumentOptions): ParcelReportDocumentInput {
	return new ParcelReportBuilder(options).build()
}

export function parcelReportMapViews(item: Record<string, unknown>, zones: Zone[]): ParcelMapView[] {
	const hasPolicy =
		POLICY_FLAGS.some(flag => item[flag.key] === true) ||
		item.policy_spi_area === true ||
		item.policy_planif_area === true
	return [
		ParcelMapView.CoverLocation,
		ParcelMapView.CloseUp,
		...(zones.length ? [ParcelMapView.Zoning] : []),
		...(item.env_floodplain_bool === true ? [ParcelMapView.FloodableZones] : []),
		...(item.env_wetland_bool === true ? [ParcelMapView.Wetlands] : []),
		...(hasPolicy ? [ParcelMapView.PlanningPolicy] : []),
		...(item.env_species_bool === true ? [ParcelMapView.EndangeredSpecies] : []),
	]
}
